import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Chip
} from '@mui/material';
import { FaExclamationTriangle } from 'react-icons/fa';
import {
  ASSIGNMENT_STATUSES,
  getAssignmentStatus
} from '../../../constants/assignmentStatuses';

const MAX_LISTED_ASSIGNEES = 8;

/**
 * ADR-014: confirmation dialog for the snapshot close ("close with unfinished work").
 *
 * The action is irreversible — there is no reopen — so the dialog must make
 * the consequences unmissable: it lists the assignments that are still open
 * (they will be frozen as-is, never completed within this cycle) and requires
 * a non-empty reason before the confirm button enables.
 */
const CloseSessionWithIncompleteModal = ({ isOpen, onClose, onConfirm, session }) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) {
      setReason('');
    }
  }, [isOpen]);

  const incompleteAssignments = useMemo(() => {
    const assignments = session?.assignments || [];
    return assignments.filter(
      (assignment) => getAssignmentStatus(assignment) !== ASSIGNMENT_STATUSES.COMPLETED
    );
  }, [session]);

  const listedAssignees = incompleteAssignments.slice(0, MAX_LISTED_ASSIGNEES);
  const overflowCount = incompleteAssignments.length - listedAssignees.length;

  const trimmedReason = reason.trim();

  const handleConfirm = () => {
    if (!trimmedReason) {
      return;
    }
    onConfirm(trimmedReason);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <FaExclamationTriangle color="#D97706" />
        Close Session with Unfinished Work
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          You are about to close the feedback session
          {' '}<strong>&quot;{session?.name}&quot;</strong>{' '}
          while it still has incomplete assignments.
        </Typography>

        <Box
          sx={{
            backgroundColor: '#FEF3C7',
            border: '1px solid #FDE047',
            borderRadius: '8px',
            p: 2,
            mb: 2
          }}
        >
          <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '14px' }}>
            This action cannot be undone
          </Typography>
          <Typography sx={{ fontSize: '14px', mb: incompleteAssignments.length > 0 ? 1.5 : 0 }}>
            The current state is preserved exactly as-is: incomplete assignments and
            reviews stay incomplete forever, reviewers can no longer submit, and the
            session cannot be reopened.
          </Typography>

          {incompleteAssignments.length > 0 && (
            <>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>
                {incompleteAssignments.length}
                {incompleteAssignments.length === 1 ? ' assignment ' : ' assignments '}
                still open:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {listedAssignees.map((assignment) => (
                  <Chip
                    key={assignment.id}
                    size="small"
                    label={assignment.employee?.fullName || 'Unknown assignee'}
                    sx={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #FDE047',
                      fontSize: '12px'
                    }}
                  />
                ))}
                {overflowCount > 0 && (
                  <Chip
                    size="small"
                    label={`and ${overflowCount} more`}
                    sx={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #FDE047',
                      fontSize: '12px',
                      fontStyle: 'italic'
                    }}
                  />
                )}
              </Box>
            </>
          )}
        </Box>

        <TextField
          label="Reason for closing (required)"
          placeholder="e.g. Review cycle ended — remaining reviewers unavailable"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          fullWidth
          multiline
          minRows={2}
          required
          autoFocus
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!trimmedReason}
          sx={{
            backgroundColor: '#D97706',
            '&:hover': { backgroundColor: '#B45309' }
          }}
        >
          Close with Unfinished Work
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloseSessionWithIncompleteModal;
